import React, { useState, useEffect } from 'react';
import { CornerUpLeft } from 'lucide-react';
import { getFileUrl, toggleFileProtection, deleteMessage } from '../services/api';
import LazyImage from './LazyImage';
import AttendanceMessage from './AttendanceMessage';
import BanNotificationMessage from './BanNotificationMessage';
import MarketplaceCodeMessage from './MarketplaceCodeMessage';
import VoicemailMessage from './VoicemailMessage';
import VoiceMessage from './VoiceMessage';
import '../styles/Message.css';

function Message({ message, isOwn, isFirst, isLast, showAvatar = true, inGroup = false, onImageLoad, currentUser, onReply, onDelete, onAllModeNavigate }) {
  const [fileProtection, setFileProtection] = useState(
    message.files?.reduce((acc, file) => {
      acc[file.id] = file.isProtected;
      return acc;
    }, {}) || {}
  );
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxType, setLightboxType] = useState('image');
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const carouselRef = React.useRef(null);

  // ── Зум фото в просмотрщике (щипок, перетаскивание, двойной тап) ──
  // Браузерный зум в чате отключён (user-scalable=no), поэтому масштабируем сами:
  // transform применяем напрямую к <img> — так плавнее, чем через состояние React.
  const currentImgRef = React.useRef(null);
  const zoomRef  = React.useRef({ scale: 1, tx: 0, ty: 0 });
  const pinchRef = React.useRef(null);   // активный щипок двумя пальцами
  const panRef   = React.useRef(null);   // активное перетаскивание увеличенного фото
  const lastTapRef = React.useRef(0);    // время предыдущего тапа — для двойного тапа

  // Обработка кнопки "назад" для закрытия lightbox
  useEffect(() => {
    if (lightboxOpen) {
      // Добавляем запись в историю при открытии lightbox
      window.history.pushState({ lightbox: true }, '');

      const handlePopState = (e) => {
        setLightboxOpen(false);
      };

      window.addEventListener('popstate', handlePopState);

      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [lightboxOpen]);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  // Проверка прав для отображения кнопки защиты
  const canProtectFiles = currentUser && (currentUser.role === 'admin' || currentUser.role === 'moderator');
  const canDelete = currentUser && currentUser.role === 'admin';



  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm('Удалить сообщение? Медиафайлы будут удалены физически.')) return;
    try {
      await deleteMessage(message.id);
      if (onDelete) onDelete(message.id);
    } catch (err) {
      console.error('Failed to delete message:', err);
      alert('Не удалось удалить сообщение');
    }
  };

  const handleReply = () => {
    if (onReply) {
      onReply(message);
    }
  };

  const renderReplyQuote = () => {
    if (!message.replyToMessage) return null;

    const reply = message.replyToMessage;
    let quoteText;
    if (reply.messageType === 'supply_arrival') {
      try { quoteText = '📦 ' + JSON.parse(reply.content).text; } catch(e) { quoteText = '📦 Приход'; }
    } else if (reply.messageType === 'voice') {
      quoteText = '🎤 Голосовое сообщение';
    } else {
      quoteText = reply.content || (reply.messageType === 'video' ? '🎥 Видео' : reply.messageType === 'image' ? '🖼️ Изображение' : '📎 Файл');
    }

    const scrollToReply = () => {
      const el = document.getElementById(`message-${reply.id}`);
      if (!el) return;
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('highlight-message');
      setTimeout(() => el.classList.remove('highlight-message'), 2000);
    };

    return (
      <div className="message-reply-quote" onClick={onAllModeNavigate ? undefined : scrollToReply}>
        <div className="reply-quote-line"></div>
        {reply.firstImageFileId && (
          <img
            src={getFileUrl(reply.firstImageFileId)}
            alt=""
            className="reply-quote-thumb"
          />
        )}
        <div className="reply-quote-content">
          <div className="reply-quote-author">{reply.displayName}</div>
          <div className="reply-quote-text">{quoteText}</div>
        </div>
      </div>
    );
  };


  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleToggleProtection = async (fileId, e) => {
    e.stopPropagation();
    try {
      const result = await toggleFileProtection(fileId);
      setFileProtection(prev => ({
        ...prev,
        [fileId]: result.isProtected
      }));
    } catch (error) {
      console.error('Failed to toggle protection:', error);
    }
  };

  const openLightbox = (index, type) => {
    setLightboxIndex(index);
    setLightboxType(type);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    // Если lightbox открыт и в истории есть наша запись, делаем back
    if (lightboxOpen && window.history.state?.lightbox) {
      window.history.back();
    } else {
      setLightboxOpen(false);
    }
  };

  const handleDownload = () => {
    const currentItem = mediaItems[lightboxIndex];
    if (!currentItem) return;

    const link = document.createElement('a');
    link.href = getFileUrl(currentItem.id);
    link.download = currentItem.originalFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const MAX_ZOOM = 5;
  const DOUBLE_TAP_ZOOM = 2.5;

  const touchDistance = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

  const applyZoom = (animate = false) => {
    const img = currentImgRef.current;
    if (!img) return;
    const { scale, tx, ty } = zoomRef.current;
    img.style.transition = animate ? 'transform 0.2s ease-out' : 'none';
    img.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
  };

  // Не даём увести фото за края: сдвиг ограничен «излишком» размера
  const clampPan = () => {
    const img = currentImgRef.current;
    if (!img) return;
    const z = zoomRef.current;
    const viewH = img.parentElement ? img.parentElement.offsetHeight : window.innerHeight;
    const maxX = Math.max(0, (img.offsetWidth  * z.scale - window.innerWidth) / 2);
    const maxY = Math.max(0, (img.offsetHeight * z.scale - viewH) / 2);
    z.tx = Math.min(maxX, Math.max(-maxX, z.tx));
    z.ty = Math.min(maxY, Math.max(-maxY, z.ty));
  };

  const resetZoom = (animate = false) => {
    zoomRef.current = { scale: 1, tx: 0, ty: 0 };
    pinchRef.current = null;
    panRef.current = null;
    applyZoom(animate);
  };

  // Переключили фото или закрыли просмотр — масштаб сбрасываем
  useEffect(() => {
    zoomRef.current = { scale: 1, tx: 0, ty: 0 };
    pinchRef.current = null;
    panRef.current = null;
    const img = currentImgRef.current;
    if (img) {
      img.style.transition = 'none';
      img.style.transform = 'translate(0px, 0px) scale(1)';
    }
  }, [lightboxIndex, lightboxOpen]);

  // Минимальное расстояние свайпа для переключения (в пикселях)
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    // Два пальца — начинаем щипок, свайп на это время отключаем
    if (e.touches.length === 2) {
      const t1 = e.touches[0];
      const t2 = e.touches[1];
      const rect = currentImgRef.current?.getBoundingClientRect();
      pinchRef.current = {
        startDist:  touchDistance(t1, t2) || 1,
        startScale: zoomRef.current.scale,
        startTx:    zoomRef.current.tx,
        startTy:    zoomRef.current.ty,
        // центр щипка относительно центра фото — вокруг этой точки увеличиваем
        cx: (t1.clientX + t2.clientX) / 2 - (rect ? rect.left + rect.width  / 2 : 0),
        cy: (t1.clientY + t2.clientY) / 2 - (rect ? rect.top  + rect.height / 2 : 0),
      };
      panRef.current = null;
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }

    // Один палец по увеличенному фото — перетаскивание, а не листание
    if (zoomRef.current.scale > 1) {
      panRef.current = {
        startX:  e.touches[0].clientX,
        startY:  e.touches[0].clientY,
        startTx: zoomRef.current.tx,
        startTy: zoomRef.current.ty,
        moved:   false,
      };
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }

    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setSwipeOffset(0);
    setIsTransitioning(false);

    // Убираем transition для плавного следования за пальцем
    if (carouselRef.current) {
      carouselRef.current.style.transition = 'none';
    }
  };

  const onTouchMove = (e) => {
    const pinch = pinchRef.current;
    if (pinch && e.touches.length === 2) {
      const d = touchDistance(e.touches[0], e.touches[1]);
      const scale = Math.min(MAX_ZOOM, Math.max(1, pinch.startScale * (d / pinch.startDist)));
      // точка под пальцами должна остаться на месте
      const px = pinch.cx / pinch.startScale;
      const py = pinch.cy / pinch.startScale;
      zoomRef.current = {
        scale,
        tx: pinch.startTx - (scale - pinch.startScale) * px,
        ty: pinch.startTy - (scale - pinch.startScale) * py,
      };
      clampPan();
      applyZoom();
      return;
    }

    const pan = panRef.current;
    if (pan && e.touches.length === 1) {
      const dx = e.touches[0].clientX - pan.startX;
      const dy = e.touches[0].clientY - pan.startY;
      if (Math.hypot(dx, dy) > 6) pan.moved = true;
      zoomRef.current.tx = pan.startTx + (e.touches[0].clientX - pan.startX);
      zoomRef.current.ty = pan.startTy + (e.touches[0].clientY - pan.startY);
      clampPan();
      applyZoom();
      return;
    }

    if (!touchStart) return;
    const currentTouch = e.targetTouches[0].clientX;
    setTouchEnd(currentTouch);
    const offset = currentTouch - touchStart;

    // Манипулируем DOM напрямую для плавности
    if (carouselRef.current) {
      const baseOffset = -window.innerWidth;
      const totalOffset = baseOffset + offset;
      carouselRef.current.style.transform = `translateX(${totalOffset}px)`;
    }
  };

  const onTouchEnd = (e) => {
    const pinch = pinchRef.current;
    const pan   = panRef.current;
    pinchRef.current = null;
    panRef.current = null;

    // Завершился щипок или перетаскивание (палец реально двигался)
    if (pinch || (pan && pan.moved)) {
      if (zoomRef.current.scale <= 1.02) resetZoom(true);
      return;
    }

    // Двойной тап: увеличить в точке тапа / вернуть обычный размер.
    // pan без движения — это тап по уже увеличенному фото, его тоже учитываем.
    if (e && e.changedTouches && e.changedTouches.length === 1 && (pan || !touchEnd)) {
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        lastTapRef.current = 0;
        if (zoomRef.current.scale > 1) {
          resetZoom(true);
        } else {
          const img = currentImgRef.current;
          if (img) {
            const rect = img.getBoundingClientRect();
            const px = e.changedTouches[0].clientX - (rect.left + rect.width  / 2);
            const py = e.changedTouches[0].clientY - (rect.top  + rect.height / 2);
            zoomRef.current = {
              scale: DOUBLE_TAP_ZOOM,
              tx: -(DOUBLE_TAP_ZOOM - 1) * px,
              ty: -(DOUBLE_TAP_ZOOM - 1) * py,
            };
            clampPan();
            applyZoom(true);
          }
        }
        return;
      }
      lastTapRef.current = now;
    }

    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    // Включаем transition для плавной анимации
    if (carouselRef.current) {
      carouselRef.current.style.transition = 'transform 0.3s ease-out';
    }

    setIsTransitioning(true);

    if (isLeftSwipe && mediaItems.length > 1) {
      // Свайп влево - анимируем до следующего фото
      if (carouselRef.current) {
        carouselRef.current.style.transform = `translateX(${-2 * window.innerWidth}px)`;
      }

      // После анимации обновляем индекс и возвращаем на базовую позицию
      setTimeout(() => {
        const newIndex = (lightboxIndex + 1) % mediaItems.length;
        setLightboxIndex(newIndex);
        setLightboxType(mediaItems[newIndex].mimeType.startsWith('video/') ? 'video' : 'image');

        if (carouselRef.current) {
          carouselRef.current.style.transition = 'none';
          carouselRef.current.style.transform = `translateX(${-window.innerWidth}px)`;
        }

        setTimeout(() => setIsTransitioning(false), 50);
      }, 300);

    } else if (isRightSwipe && mediaItems.length > 1) {
      // Свайп вправо - анимируем до предыдущего фото
      if (carouselRef.current) {
        carouselRef.current.style.transform = `translateX(0px)`;
      }

      // После анимации обновляем индекс и возвращаем на базовую позицию
      setTimeout(() => {
        const newIndex = (lightboxIndex - 1 + mediaItems.length) % mediaItems.length;
        setLightboxIndex(newIndex);
        setLightboxType(mediaItems[newIndex].mimeType.startsWith('video/') ? 'video' : 'image');

        if (carouselRef.current) {
          carouselRef.current.style.transition = 'none';
          carouselRef.current.style.transform = `translateX(${-window.innerWidth}px)`;
        }

        setTimeout(() => setIsTransitioning(false), 50);
      }, 300);

    } else {
      // Недостаточный свайп - возвращаем на место
      if (carouselRef.current) {
        carouselRef.current.style.transform = `translateX(${-window.innerWidth}px)`;
      }
      setTimeout(() => setIsTransitioning(false), 300);
    }

    setSwipeOffset(0);
  };

  const images = message.files?.filter(f => f.mimeType.startsWith('image/')) || [];
  const videos = message.files?.filter(f => f.mimeType.startsWith('video/')) || [];
  const documents = message.files?.filter(f =>
    !f.mimeType.startsWith('image/') && !f.mimeType.startsWith('video/')
  ) || [];

  // Объединяем изображения и видео для галереи
  const mediaItems = [...images, ...videos];

  // Получаем 3 видимых элемента для карусели (prev, current, next)
  const getVisibleMedia = () => {
    if (mediaItems.length === 0) return [null, null, null];
    if (mediaItems.length === 1) return [mediaItems[0], mediaItems[0], mediaItems[0]];

    const prevIndex = (lightboxIndex - 1 + mediaItems.length) % mediaItems.length;
    const nextIndex = (lightboxIndex + 1) % mediaItems.length;

    return [
      mediaItems[prevIndex],
      mediaItems[lightboxIndex],
      mediaItems[nextIndex]
    ];
  };

  const renderMediaGallery = () => {
    if (mediaItems.length === 0) return null;

    const galleryClass = `media-gallery grid-${Math.min(mediaItems.length, 4)}`;

    return (
      <div className={galleryClass}>
        {mediaItems.slice(0, 9).map((file, index) => {
          const isVideo = file.mimeType.startsWith('video/');
          const isImage = file.mimeType.startsWith('image/');

          return (
            <div
              key={file.id}
              className={`media-item ${isVideo ? 'media-item-video' : ''}`}
              onClick={onAllModeNavigate ? undefined : (e) => { e.stopPropagation(); openLightbox(index, isVideo ? 'video' : 'image'); }}
            >
              {isImage && (
                <LazyImage
                  src={getFileUrl(file.id)}
                  alt={file.originalFilename}
                  onImageLoad={onImageLoad}
                />
              )}
              {isVideo && (
                <>
                  <video preload="metadata" muted playsInline>
                    <source src={getFileUrl(file.id)} type={file.mimeType} />
                  </video>
                  <div className="video-play-overlay">
                    <span className="video-play-icon">▶</span>
                  </div>
                </>
              )}
              {index === 8 && mediaItems.length > 9 && (
                <div className="media-overlay">
                  <span className="media-count">+{mediaItems.length - 9}</span>
                </div>
              )}
              {canProtectFiles && (
                <button
                  className={`btn-protect-mini ${fileProtection[file.id] ? 'protected' : ''}`}
                  onClick={(e) => handleToggleProtection(file.id, e)}
                  title={fileProtection[file.id] ? 'Защищено' : 'Не защищено'}
                >
                  {fileProtection[file.id] ? '🔒' : '🔓'}
                </button>
              )}
              {isImage && (
                <div className="file-id-badge" onClick={(e) => e.stopPropagation()}>
                  #{file.id}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderDocuments = () => {
    if (documents.length === 0) return null;

    return (
      <div className="message-documents">
        {documents.map((file) => (
          <div key={file.id} className="document-item">
            <div className="document-icon">📄</div>
            <div className="document-info">
              <a href={getFileUrl(file.id)} download={file.originalFilename}>
                {file.originalFilename}
              </a>
              <span className="document-size">{formatFileSize(file.fileSize)}</span>
            </div>
            {canProtectFiles && (
              <button
                className={`btn-protect-mini ${fileProtection[file.id] ? 'protected' : ''}`}
                onClick={(e) => handleToggleProtection(file.id, e)}
              >
                {fileProtection[file.id] ? '🔒' : '🔓'}
              </button>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Спецсообщение: форма явки
  if (message.messageType === 'attendance') {
    return (
      <div id={`message-${message.id}`} style={{ padding: '4px 12px', boxSizing: 'border-box', width: '100%' }}>
        <AttendanceMessage message={message} currentUser={currentUser} />
      </div>
    );
  }

  // Спецсообщение: код маркетплейса
  if (message.messageType === 'marketplace_code') {
    return (
      <div id={`message-${message.id}`} style={{ padding: '4px 12px', boxSizing: 'border-box', width: '100%' }}>
        <MarketplaceCodeMessage message={message} />
      </div>
    );
  }

  // Спецсообщение: голосовое с автоответчика телефонии
  if (message.messageType === 'voicemail') {
    return (
      <div id={`message-${message.id}`} style={{ padding: '4px 12px', boxSizing: 'border-box', width: '100%' }}>
        <VoicemailMessage message={message} />
      </div>
    );
  }

  // Спецсообщение: бан антиспама
  if (message.messageType === 'ban_notification') {
    return (
      <div id={`message-${message.id}`} style={{ padding: '4px 12px', boxSizing: 'border-box', width: '100%' }}>
        <BanNotificationMessage message={message} />
      </div>
    );
  }

  // Голосовое сообщение из чата — в обычном пузыре, чтобы работали ответ/удаление
  if (message.messageType === 'voice') {
    const bubbleClassVoice = `message-bubble ${isFirst ? 'first' : ''} ${isLast ? 'last' : ''} ${inGroup ? 'in-group' : ''}`;
    return (
      <div
        id={`message-${message.id}`}
        className={`message ${isOwn ? 'own' : 'other'}`}
        onClick={onAllModeNavigate || undefined}
        style={onAllModeNavigate ? { cursor: 'pointer' } : undefined}
      >
        {!isOwn && showAvatar && (
          <div className="message-avatar">{[...message.displayName][0].toUpperCase()}</div>
        )}
        <div className={bubbleClassVoice}>
          {renderReplyQuote()}
          <VoiceMessage message={message} isOwn={isOwn} />
          <div className="message-footer">
            <span className="message-time">{formatTime(message.createdAt)}</span>
            <div className="message-actions">
              {onReply && (
                <button className="btn-reply" onClick={handleReply} title="Ответить">
                  <CornerUpLeft size={14} strokeWidth={1.8} />
                </button>
              )}
              {canDelete && (
                <button className="btn-reply btn-delete-msg" onClick={handleDelete} title="Удалить">
                  🗑
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Спецсообщение: приход товара
  if (message.messageType === 'supply_arrival') {
    let supplyData = {};
    try { supplyData = JSON.parse(message.content); } catch(e) { supplyData = { text: message.content }; }
    const bubbleClassSupply = `message-bubble ${isFirst ? 'first' : ''} ${isLast ? 'last' : ''} ${inGroup ? 'in-group' : ''}`;
    return (
      <div
        id={`message-${message.id}`}
        className={`message ${isOwn ? 'own' : 'other'}`}
        onClick={onAllModeNavigate || undefined}
        style={onAllModeNavigate ? { cursor: 'pointer' } : undefined}
      >
        {!isOwn && showAvatar && (
          <div className="message-avatar">{[...message.displayName][0].toUpperCase()}</div>
        )}
        <div className={bubbleClassSupply}>
          {renderReplyQuote()}
          <div className="supply-arrival-card">
            {supplyData.photo && (
              <img
                src={`/supply-photos/${supplyData.photo}`}
                alt=""
                className="supply-arrival-thumb"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="supply-arrival-text">{supplyData.text}</div>
          </div>
          <div className="message-footer">
            <span className="message-time">{formatTime(message.createdAt)}</span>
            <div className="message-actions">
              {onReply && (
                <button className="btn-reply" onClick={handleReply} title="Ответить">
                  <CornerUpLeft size={14} strokeWidth={1.8} />
                </button>
              )}
              {canDelete && (
                <button className="btn-reply btn-delete-msg" onClick={handleDelete} title="Удалить">
                  🗑
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const bubbleClass = `message-bubble ${isFirst ? 'first' : ''} ${isLast ? 'last' : ''} ${inGroup ? 'in-group' : ''}`;

  return (
    <>
      <div
        id={`message-${message.id}`}
        className={`message ${isOwn ? 'own' : 'other'}`}
        onClick={onAllModeNavigate || undefined}
        style={onAllModeNavigate ? { cursor: 'pointer' } : undefined}
      >
        {!isOwn && showAvatar && (
          <div className="message-avatar">
            {[...message.displayName][0].toUpperCase()}
          </div>
        )}

        <div className={bubbleClass}>
          {renderReplyQuote()}
          {renderMediaGallery()}
          {renderDocuments()}

          {message.content && (
            <div className="message-text">{message.content}</div>
          )}

          <div className="message-footer">
            <span className="message-time">{formatTime(message.createdAt)}</span>
            {message.editedAt && <span className="message-edited">изменено</span>}
            {isOwn && (
              <span className="message-status">
                <span className="status-icon">✓</span>
              </span>
            )}
            <div className="message-actions">
              {onReply && (
                <button className="btn-reply" onClick={handleReply} title="Ответить">
                  <CornerUpLeft size={14} strokeWidth={1.8} />
                </button>
              )}
              {canDelete && (
                <button className="btn-reply btn-delete-msg" onClick={handleDelete} title="Удалить">
                  🗑
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {lightboxOpen && (() => {
        const [prevItem, currentItem, nextItem] = getVisibleMedia();
        // Базовая позиция -innerWidth центрирует на текущем изображении
        const baseOffset = -window.innerWidth;
        const totalOffset = baseOffset + swipeOffset;

        return (
          <div className="lightbox" onClick={closeLightbox}>
            <div className="lightbox-content" style={{ height: '100%', width: '100%', overflow: 'hidden', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
              <button className="lightbox-close" style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 1000 }} onClick={closeLightbox}>✕</button>
              <button className="lightbox-download" style={{ position: 'absolute', top: '10px', right: '60px', zIndex: 1000, background: 'rgba(0, 0, 0, 0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '40px', height: '40px', fontSize: '20px', cursor: 'pointer' }} onClick={handleDownload} title="Скачать">⬇</button>

              <div
                ref={carouselRef}
                className="lightbox-carousel"
                style={{
                  transform: `translateX(${totalOffset}px)`,
                  transition: 'none',
                  display: 'flex',
                  height: '100%',
                  willChange: 'transform',
                  backfaceVisibility: 'hidden',
                  WebkitBackfaceVisibility: 'hidden',
                  touchAction: 'none'
                }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                {/* Предыдущее изображение */}
                <div key={`slide-prev-${prevItem?.id || 'empty'}`} className="lightbox-slide" style={{ width: window.innerWidth + 'px', height: '100%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {prevItem && (
                    prevItem.mimeType.startsWith('image/') ? (
                      <img key={`img-${prevItem.id}`} src={getFileUrl(prevItem.id)} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                      <video key={`video-${prevItem.id}`} controls style={{ maxWidth: '100%', maxHeight: '100%' }}>
                        <source src={getFileUrl(prevItem.id)} type={prevItem.mimeType} />
                      </video>
                    )
                  )}
                </div>

                {/* Текущее изображение */}
                <div key={`slide-current-${currentItem?.id || 'empty'}`} className="lightbox-slide" style={{ width: window.innerWidth + 'px', height: '100%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {currentItem && (
                    currentItem.mimeType.startsWith('image/') ? (
                      <img ref={currentImgRef} key={`img-${currentItem.id}`} src={getFileUrl(currentItem.id)} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                      <video key={`video-${currentItem.id}`} controls autoPlay style={{ maxWidth: '100%', maxHeight: '100%' }}>
                        <source src={getFileUrl(currentItem.id)} type={currentItem.mimeType} />
                      </video>
                    )
                  )}
                </div>

                {/* Следующее изображение */}
                <div key={`slide-next-${nextItem?.id || 'empty'}`} className="lightbox-slide" style={{ width: window.innerWidth + 'px', height: '100%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {nextItem && (
                    nextItem.mimeType.startsWith('image/') ? (
                      <img key={`img-${nextItem.id}`} src={getFileUrl(nextItem.id)} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    ) : (
                      <video key={`video-${nextItem.id}`} controls style={{ maxWidth: '100%', maxHeight: '100%' }}>
                        <source src={getFileUrl(nextItem.id)} type={nextItem.mimeType} />
                      </video>
                    )
                  )}
                </div>
              </div>

              <div className="lightbox-controls">
              <button
                className="lightbox-prev"
                onClick={() => {
                  const newIndex = (lightboxIndex - 1 + mediaItems.length) % mediaItems.length;
                  setLightboxIndex(newIndex);
                  setLightboxType(mediaItems[newIndex].mimeType.startsWith('video/') ? 'video' : 'image');
                }}
                disabled={mediaItems.length === 1}
              >
                ‹
              </button>
              <span className="lightbox-counter">
                {lightboxIndex + 1} / {mediaItems.length}
              </span>
              <button
                className="lightbox-next"
                onClick={() => {
                  const newIndex = (lightboxIndex + 1) % mediaItems.length;
                  setLightboxIndex(newIndex);
                  setLightboxType(mediaItems[newIndex].mimeType.startsWith('video/') ? 'video' : 'image');
                }}
                disabled={mediaItems.length === 1}
              >
                ›
              </button>
            </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}

export default Message;
